import type { ExecutivePeriodKind, ExecutiveQuery } from '../dashboard-v2/types';

export const PERIOD_OPTIONS: { kind: ExecutivePeriodKind; label: string }[] = [
  { kind: 'thisMonth', label: 'This month' },
  { kind: 'lastMonth', label: 'Last month' },
  { kind: 'thisQuarter', label: 'This quarter' },
  { kind: 'yearToDate', label: 'Year to date' },
  { kind: 'trailing12Months', label: 'Last 12 months' },
  { kind: 'custom', label: 'Custom range' },
];

export const MAX_CUSTOM_MONTHS = 24;

const PERIOD_KINDS = PERIOD_OPTIONS.map((o) => o.kind);
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** `2026-03` -> `2026-03-01`. */
export const monthToFirstDay = (month: string) => `${month}-01`;

/** `2026-03` -> `2026-03-31`. */
export function monthToLastDay(month: string): string {
  const [year, m] = month.split('-').map(Number);
  return `${month}-${String(new Date(Date.UTC(year!, m!, 0)).getUTCDate()).padStart(2, '0')}`;
}

/** `2026-03-31` -> `2026-03`. */
export const dayToMonth = (day: string | undefined) => (day ? day.slice(0, 7) : '');

/** How many calendar months a `from`..`to` pair of `YYYY-MM` values covers, counting both ends. */
export function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty! - fy!) * 12 + tm! - fm! + 1;
}

/** Reads the chosen period from the page address, falling back to this month for anything that is not a valid choice. */
export function parseExecutiveQuery(params: URLSearchParams): ExecutiveQuery {
  const period = params.get('period') as ExecutivePeriodKind | null;
  if (!period || !PERIOD_KINDS.includes(period)) return { period: 'thisMonth' };
  if (period !== 'custom') return { period };
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && ISO_DAY.test(from) && ISO_DAY.test(to) && from <= to) return { period, from, to };
  // A custom period with no usable dates shows the pickers, and asks the server for nothing until both months are chosen.
  return { period: 'custom' };
}

/** The address parameters for a period choice. This month is the default and needs none. */
export function toParams(query: ExecutiveQuery): Record<string, string> {
  if (query.period === 'thisMonth') return {};
  return query.period === 'custom' && query.from && query.to
    ? { period: 'custom', from: query.from, to: query.to }
    : { period: query.period };
}
